/**
 * FORGOT PASSWORD
 *
 * Used when a public user can't log into their account because they can't remember their
 * password perhaps their password wasn't ever set. Submitting this form will trigger an email
 * to be sent to the user so they can reset their password.
 *
 * @returns {Promise<void>}
 */
const main = async () => {
    const [
        {default: Vue},
        {ModalPlugin},
        {AjaxMixin},
        ErrorMessages,
        {ValidationProvider, ValidationObserver, extend, configure, localize},
        {required, email},
        {emailTld},
        VueScrollTo,
    ] = await Promise.all([
        import(/* webpackChunkName: "vue" */ 'vue'),
        import(/* webpackChunkName: "bs-modal" */ 'bootstrap-vue'),
        import(/* webpackChunkName: "global-mixins-ajax" */ '@mymyMixins/ajax'),
        import(/* webpackChunkName: "global-translations-validation" */ '@mymyTranslations/validation'),
        import(/* webpackChunkName: "vee-validate" */ 'vee-validate'),
        import(/* webpackChunkName: "vee-validate-rules" */ 'vee-validate/dist/rules'),
        import(/* webpackChunkName: "global-validators" */ '@mymyValidators'),
        import(/* webpackChunkName: "vue-scrollto" */ 'vue-scrollto'),
    ]);

    Vue.use(ModalPlugin);

    // for scrolling
    Vue.use(VueScrollTo, {
        offset: -110,
    });

    // load our translated form error messages for this page's language
    localize(window.language, ErrorMessages.default[window.language]);

    // add our Vee Validate validators (email is already loaded)
    extend('required', required);
    extend('email', email);
    extend('emailTld', emailTld);

    // tell Vee Validate which classes to apply when a field is valid/invalid, etc
    configure({ classes: { invalid: 'is-invalid' } });

    const vm = new Vue({
        name: 'ForgotPasswordForm',
        el: '#forgotPassword',
        delimiters: ['[[', ']]'],
        mixins: [AjaxMixin],
        components: {
            'validation': ValidationProvider,
            'validation-observer': ValidationObserver,
        },
        data: {
            isLoading: false,
            loginName: '',
            formSuccess: false,
            serverErrors: [],
        },
        mounted() {
            let form = JSON.parse(this.$el.dataset.form);
            this.loginName = form.loginName;
        },
        methods: {

            // EVENTS
            // =====================================================================================

            /**
             * Submit the form to Craft's controller and handle the response.
             *
             * @return {Promise<void>}
             */
            async onSubmit() {
                this.isLoading = true;
                this.serverErrors = [];

                // send the data to Craft's controller
                const response = await this.makePostRequest('users/send-password-reset-email', {
                    loginName: this.loginName,
                });

                if (response?.data?.message) {
                    this.formSuccess = true;
                    this.isLoading = false;
                }
                else if (response?.data?.error) {
                    this.logErrors({message: [response.data.error]});
                }
                else if (response?.data?.errors) {
                    this.logErrors(response.data.errors);
                }
                else {
                    this.logErrors({message: ["There was an error"]});
                    if(window.Sentry) {
                        Sentry.captureMessage("Response not handled correctly")
                    }
                }
            },



            // HELPERS
            // =====================================================================================

            /**
             * METHOD: Log any errors coming back from the server so they get shown to the user
             * on the front-end.
             *
             * @param errors
             * @param formNumber
             */
            logErrors(errors) {
                this.formSuccess = false;
                this.serverErrors = [];
                for (const erroredField in errors) {
                    for (const errorMessage of errors[erroredField]) {
                        this.serverErrors.push(errorMessage);
                    }
                }
                this.isLoading = false;
            },
        },
    });

    return vm;
};

// Execute async function
main().then( (vm) => {
});

// Accept HMR as per: https://webpack.js.org/api/hot-module-replacement#accept
if (module.hot) {
    module.hot.accept();
}